import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  TextField,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ClearIcon from "@mui/icons-material/Clear";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import {
  DSOK_DYNAMIC_COMPONENTS_TYPES,
  SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_CART,
  SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_PRODUCT_DETAIL,
  SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_PRODUCTS_GRID,
  SMARTKIOSK_DYNAMIC_COMPONENTS_VIEWS,
} from "app/utils/constants/settings";

const FIELD_NAME = "DYNAMIC_COMPONENTS_FIELD";

const SmartkioskDynamicComponents = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [propsData, setPropsData] = useState({});
  const [categoriesData, setCategoriesData] = useState({});
  const [categoriesJsonText, setCategoriesJsonText] = useState({});
  const [propsJsonText, setPropsJsonText] = useState({});
  const [selectedTabSettings, setSelectedTabSettings] = useState("1");

  const { showDialog, hideDialog } = useJumboDialog();

  const tabSettingsKeys = Object.keys(
    values.settings.smartkiosk.dynamicComponents || {}
  );

  const handleAddTabSettings = () => {
    setFieldValue("settings.smartkiosk.dynamicComponents", {
      ...values.settings.smartkiosk.dynamicComponents,
      [tabSettingsKeys.length + 1]: {},
    });
  };

  const handleDeleteTabSettings = (index) => {
    const newTabSettings = {};
    let newIndex = 1;
    tabSettingsKeys.forEach((key) => {
      if (key !== index) {
        newTabSettings[newIndex] =
          values.settings.smartkiosk.dynamicComponents[key];
        newIndex++;
      }
    });
    setFieldValue("settings.smartkiosk.dynamicComponents", newTabSettings);
    setSelectedTabSettings(Object.keys(newTabSettings)[0] || "1");
    hideDialog();
  };

  const handlePropsChange = (value, tabSettingsKey) => {
    setPropsData(value);
    setFieldValue(
      `settings.smartkiosk.dynamicComponents.${tabSettingsKey}.props`,
      value
    );
  };
  const handleCategoriesChange = (value, tabSettingsKey) => {
    setCategoriesData(value);
    setFieldValue(
      `settings.smartkiosk.dynamicComponents.${tabSettingsKey}.conditions.categories`,
      value
    );
  };

  const content = (
    <Grid item xs={12} md={12} lg={12}>
      <AccordionDetails>
        <Stack
            direction="row"
            alignItems="center"
            width="100%"
            spacing={1}
            px={2}
          >
            <TabContext value={selectedTabSettings}>
              <Stack width="100%">
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <TabList onChange={(_, val) => setSelectedTabSettings(val)}>
                    {tabSettingsKeys.map((item) => {
                      return (
                        <Tab
                          key={item}
                          label={
                            <Stack
                              direction="row"
                              sx={{ alignItems: "center" }}
                            >
                              {values.settings.smartkiosk.dynamicComponents[
                                item
                              ].name || item}
                              <IconButton
                                onClick={() =>
                                  showDialog({
                                    variant: "confirm",
                                    title: t(
                                      "widgets.confirmDialog.areYouSure"
                                    ),
                                    onYes: () => handleDeleteTabSettings(item),
                                    onNo: () => hideDialog(),
                                  })
                                }
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                          value={item}
                        />
                      );
                    })}
                  </TabList>

                  <IconButton onClick={handleAddTabSettings}>
                    <AddCircleIcon fontSize="small" />
                  </IconButton>
                </Box>
                {!tabSettingsKeys?.length ? (
                  <JumboListNoDataPlaceholder />
                ) : (
                  tabSettingsKeys.map((tabSettingsKey) => {
                    let positions = [];
                    const view =
                      values?.settings?.smartkiosk?.dynamicComponents?.[
                        tabSettingsKey
                      ]?.conditions?.view || null;
                    switch (view) {
                      case "cart":
                        positions =
                          SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_CART;
                        break;
                      case "product-detail":
                        positions =
                          SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_PRODUCT_DETAIL;
                        break;
                      case "products-grid":
                        positions =
                          SMARTKIOSK_DYNAMIC_COMPONENTS_POSITIONS_PRODUCTS_GRID;
                        break;
                      default:
                        positions = [];
                        break;
                    }
                    return (
                      <TabPanel
                        key={`tab_${tabSettingsKey}`}
                        value={tabSettingsKey}
                        sx={{
                          pb: 1,
                          pr: 0,
                          pl: 0,
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={4} lg={2}>
                            <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                              <JumboTextField
                                select
                                required
                                InputLabelProps={{ required: false }}
                                name={`settings.smartkiosk.dynamicComponents.${tabSettingsKey}.name`}
                                label={
                                  <Label
                                    text={t(
                                      "pages.dsokDetails.dynamicComponents.name"
                                    )}
                                    required
                                  />
                                }
                              >
                                {DSOK_DYNAMIC_COMPONENTS_TYPES.map(
                                  (type, index) => {
                                    return (
                                      <MenuItem key={index} value={type.code}>
                                        {type.name}
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </JumboTextField>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4} lg={2}>
                            <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                              <JumboTextField
                                select
                                InputLabelProps={{ required: false }}
                                name={`settings.smartkiosk.dynamicComponents.${tabSettingsKey}.conditions.view`}
                                defaultValue="all"
                                label={t(
                                  "pages.dsokDetails.dynamicComponents.conditions.view"
                                )}
                              >
                                {SMARTKIOSK_DYNAMIC_COMPONENTS_VIEWS.map(
                                  (type, index) => {
                                    return (
                                      <MenuItem key={index} value={type.code}>
                                        {type.name}
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </JumboTextField>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4} lg={2}>
                            <FormControl sx={{ mt: 0, mb: 0 }} fullWidth>
                              <JumboTextField
                                select
                                required
                                InputLabelProps={{ required: false }}
                                name={`settings.smartkiosk.dynamicComponents.${tabSettingsKey}.conditions.position`}
                                defaultValue={positions[0]?.code || null}
                                label={t(
                                  "pages.dsokDetails.dynamicComponents.conditions.position"
                                )}
                              >
                                {positions.map((type, index) => {
                                  return (
                                    <MenuItem key={index} value={type.code}>
                                      {type.name}
                                    </MenuItem>
                                  );
                                })}
                              </JumboTextField>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={12} lg={12}>
                            <Alert severity="info">
                              <AlertTitle>
                                {t(
                                  "pages.dsokDetails.dynamicComponents.conditions.categoriesHelp"
                                )}
                              </AlertTitle>
                              {t(
                                "pages.dsokDetails.dynamicComponents.conditions.categoriesHelpDescription"
                              )}
                            </Alert>
                            <Div sx={{ mt: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                value={
                                  categoriesJsonText[tabSettingsKey] ??
                                  (() => {
                                    const raw =
                                      values?.settings?.smartkiosk
                                        ?.dynamicComponents?.[tabSettingsKey]
                                        ?.conditions?.categories ?? null;
                                    try {
                                      return raw === null || raw === undefined
                                        ? ""
                                        : typeof raw === "object"
                                          ? JSON.stringify(raw, null, 2)
                                          : String(raw);
                                    } catch {
                                      return String(raw ?? "");
                                    }
                                  })()
                                }
                                onChange={(e) => {
                                  const text = e.target.value;
                                  setCategoriesJsonText((prev) => ({
                                    ...prev,
                                    [tabSettingsKey]: text,
                                  }));
                                  try {
                                    const parsed =
                                      text.trim() === "" ? null : JSON.parse(text);
                                    if (
                                      parsed === null ||
                                      typeof parsed === "object"
                                    ) {
                                      handleCategoriesChange(
                                        parsed,
                                        tabSettingsKey
                                      );
                                      setCategoriesJsonText((prev) => {
                                        const next = { ...prev };
                                        delete next[tabSettingsKey];
                                        return next;
                                      });
                                    }
                                  } catch {
                                    // Invalid JSON: keep text in state
                                  }
                                }}
                                placeholder="{}"
                                sx={{
                                  "& textarea": {
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                  },
                                }}
                              />
                            </Div>
                          </Grid>
                          <Grid item xs={12} md={12} lg={12}>
                            <Alert severity="info">
                              <AlertTitle>
                                {t(
                                  "pages.dsokDetails.dynamicComponents.props.help.title"
                                )}
                              </AlertTitle>
                              {t(
                                "pages.dsokDetails.dynamicComponents.props.help.description"
                              )}
                            </Alert>
                            <Div sx={{ mt: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                value={
                                  propsJsonText[tabSettingsKey] ??
                                  (() => {
                                    const raw =
                                      values?.settings?.smartkiosk
                                        ?.dynamicComponents?.[tabSettingsKey]
                                        ?.props ?? null;
                                    try {
                                      return raw === null || raw === undefined
                                        ? ""
                                        : typeof raw === "object"
                                          ? JSON.stringify(raw, null, 2)
                                          : String(raw);
                                    } catch {
                                      return String(raw ?? "");
                                    }
                                  })()
                                }
                                onChange={(e) => {
                                  const text = e.target.value;
                                  setPropsJsonText((prev) => ({
                                    ...prev,
                                    [tabSettingsKey]: text,
                                  }));
                                  try {
                                    const parsed =
                                      text.trim() === "" ? null : JSON.parse(text);
                                    if (
                                      parsed === null ||
                                      typeof parsed === "object"
                                    ) {
                                      handlePropsChange(parsed, tabSettingsKey);
                                      setPropsJsonText((prev) => {
                                        const next = { ...prev };
                                        delete next[tabSettingsKey];
                                        return next;
                                      });
                                    }
                                  } catch {
                                    // Invalid JSON: keep text in state
                                  }
                                }}
                                placeholder="{}"
                                sx={{
                                  "& textarea": {
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                  },
                                }}
                              />
                            </Div>
                          </Grid>
                        </Grid>
                      </TabPanel>
                    );
                  })
                )}
              </Stack>
            </TabContext>
          </Stack>
        </AccordionDetails>
      </Grid>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.dynamicComponents.title")}
        </Typography>
      </AccordionSummary>
      {content}
    </Accordion>
  );
};

export default SmartkioskDynamicComponents;
